/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Injector } from '@angular/core';
import { DatePipe } from '@angular/common';
import { formatLogString } from 'utils/lib/common/common-utils';
import { formatLogTracer } from 'utils/lib/log-tracer/log-tracer';
import { RemoteLoggingService } from '../remote-logging/services/remote-logging.service';
import { LoggingLevel } from './logging-types';
import { forwardLogToNative, setNativeLogForwardingEnabled } from './native-log-forwarder';

function shouldForwardToNative(level: LoggingLevel): boolean {
    return level === LoggingLevel.Error || level === LoggingLevel.Warning || level === LoggingLevel.Info;
}

declare const require: any;

@Injectable()
export class LoggerConfig {
    defaultLoggingLevel: LoggingLevel;
    appNamePrefix?: string;
}

export class Logger {
    private remoteLogging: boolean;
    private consoleLogging: boolean;
    private isSafari: boolean;

    constructor(
        private name: string,
        private loggingLevel: LoggingLevel,
        private datePipe: DatePipe,
        private remoteLoggingService: RemoteLoggingService,
        private remoteLoggingEnabled: boolean,
        private consoleLoggingEnabled: boolean
    ) {
        this.remoteLogging = remoteLoggingEnabled;
        this.consoleLogging = consoleLoggingEnabled;
    }

    /**
     * Logs an error
     *
     * @param text Text to log
     */
    error(...text: any[]): void {
        this.logEntry(LoggingLevel.Error, text, console.error.bind(console));
    }

    /**
     * Logs a warning
     *
     * @param text Text to log
     */
    warn(...text: any[]): void {
        this.logEntry(LoggingLevel.Warning, text, console.warn.bind(console));
    }

    /**
     * Logs informational text
     *
     * @param text Text to log
     */
    info(...text: any[]): void {
        this.logEntry(LoggingLevel.Info, text, console.log.bind(console));
    }

    /**
     * Logs verbose text
     *
     * @param text Text to log
     */
    verbose(...text: any[]): void {
        this.logEntry(LoggingLevel.Verbose, text, console.log.bind(console));
    }

    /**
     * Logs event text
     *
     * @param text Text to log
     */
    event(...text: any[]): void {
        this.logEntry(LoggingLevel.Event, text, console.log.bind(console));
    }

    trace(...text: any[]): void {
        this.logEntry(LoggingLevel.Trace, text, console.log.bind(console));
    }

    updateLogger(remoteLoggingEnabled: boolean, consoleLoggingEnabled: boolean) {
        this.remoteLogging = remoteLoggingEnabled;
        this.consoleLogging = consoleLoggingEnabled;
    }

    private logEntry(level: LoggingLevel, logArgs: any[], logFn: Function): void {
        if (this.loggingLevel < level) {
            return;
        }
        let logString = '';
        if (level === LoggingLevel.Trace) {
            logString = formatLogTracer(this.name, LoggingLevel[level].toUpperCase(), this.datePipe, logArgs);
        } else {
            logString = formatLogString(this.name, LoggingLevel[level].toUpperCase(), this.datePipe, logArgs);
        }
        // Console logging should be enabled based on feature config flag
        if (this.consoleLogging) {
            logFn(logString);
        }
        // Remote logging should be enabled based on feature config flag
        if (this.remoteLogging) {
            this.remoteLoggingService.collectLogString(logString);
        }
        if (shouldForwardToNative(level)) {
            forwardLogToNative(LoggingLevel[level].toUpperCase(), this.name, logString);
        }
    }
}

interface LoggingOverrides {
    [key: string]: string;
}

// Each logger has it's own logging level defined in the service. This allows us to vary the logging level for
// each component, allowing us to set everything Error except for a particular logger we set to Verbose.

@Injectable({
    providedIn: 'root'
})
export class LoggingService {
    private loggingLevelOverrides: LoggingOverrides = {};
    private loggers: { [key: string]: Logger } = {};
    private remoteLoggingEnabled = false;
    private consoleLoggingEnabled = true;
    constructor(
        private config: LoggerConfig,
        private datePipe: DatePipe,
        private remoteLoggingService: RemoteLoggingService,
        private injector: Injector
    ) {
        this.loggingLevelOverrides = require('../../assets/logger/logger.json');
    }

    /**
     * If the remoteLogging and consoleLogging flags have changed with the intialize()
     * call then the loggers which were created before the initialize() call need
     * to be updated so that we write/send logs to console/server accordingly.
     */
    private updateLoggers() {
        Object.values(this.loggers).forEach(logger => {
            logger.updateLogger(this.remoteLoggingEnabled, this.consoleLoggingEnabled);
        });
    }

    public getLoggingLevel(loggerName: string) {
        return LoggingLevel[this.loggingLevelOverrides[loggerName]] || this.config.defaultLoggingLevel;
    }

    initialize(remoteLoggingEnabled: boolean, consoleLoggingEnabled: boolean, forwardLogsToNative = false) {
        setNativeLogForwardingEnabled(forwardLogsToNative, this.injector);
        if (remoteLoggingEnabled) {
            this.remoteLoggingService.initialize();
        }
        if (
            this.remoteLoggingEnabled !== remoteLoggingEnabled ||
            this.consoleLoggingEnabled !== consoleLoggingEnabled
        ) {
            this.remoteLoggingEnabled = remoteLoggingEnabled;
            this.consoleLoggingEnabled = consoleLoggingEnabled;
            // ConsoleLogging can be enabled/disabled based on the project feature flags.
            // Thus when consoleLogging is disabled, we clear any logs that might have been printed
            // by the time this method is called.
            if (!this.consoleLoggingEnabled) {
                console.clear();
            }
            this.updateLoggers();
        }
    }

    /**
     * Gets a Logger with the specified name. If none exists, it creates one.
     *
     * @param loggerName Name of the logging component.
     * @returns A perfectly cromulent logger.
     */
    getLogger(loggerName: string): Logger {
        // Update the log-specific overrides`
        const loggingLevel = this.getLoggingLevel(loggerName);

        if (this.config.appNamePrefix) {
            loggerName = this.config.appNamePrefix + '/' + loggerName;
        }

        if (!this.loggers[loggerName]) {
            this.loggers[loggerName] = new Logger(
                loggerName,
                loggingLevel,
                this.datePipe,
                this.remoteLoggingService,
                this.remoteLoggingEnabled,
                this.consoleLoggingEnabled
            );
        }

        return this.loggers[loggerName];
    }
}
